/* eslint-disable no-console */
import React, { useState, useRef, useCallback, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom"; // Added useParams
import {
    Card,
    CardContent,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    TextField,
    Button,
    IconButton,
    Select,
    MenuItem,
    Checkbox,
    FormGroup,
    Switch,
    Divider,
    Grid,
    InputAdornment,
    Box,
    Tooltip,
    CircularProgress, // Added for loading state
    Snackbar, // Added for feedback
    Alert, // Added for feedback
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { LocalizationProvider, TimePicker, DatePicker } from "@mui/x-date-pickers"; // Added DatePicker
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { THEME_MAP } from "../theme"; // Ensure this path is correct
import { firestore, storage, firebase } from "../services/firebase"; // Added firebase services
import { useViewedBar, useBarUtils } from "../actions/bars"; // To get barName potentially
// Assuming generateId is exported from bars actions or a utils file
import { generateId } from "../actions/bars"; // Make sure this is exported or move to utils
import { captureError } from "../lib/errorReporting";

dayjs.extend(isSameOrBefore);


// Styled Components (consistent with other views)
const StyledContainer = styled.div`
    padding: 24px;
    height: 100%;
    width: 100%;
    overflow: auto;
`;

const SectionCard = styled(Card)`
    margin-bottom: 24px;
    border: 1px solid ${THEME_MAP["color-basic-400"]};
    background: ${THEME_MAP["color-basic-100"]};
`;

const SectionTitle = styled(Typography)`
    margin-bottom: 16px !important;
    color: ${THEME_MAP["color-primary-500"]};
`;

const AddItemButton = styled(Button)`
    margin-top: 16px !important;
`;

const ItemContainer = styled(Box)`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
    width: 100%;
`;

const ImageUploadItemContainer = styled(Box)`
    display: flex;
    align-items: flex-start; // Align items to top for better layout with date picker
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap; // Allow wrapping for smaller screens
`;

const ImagePreviewContainer = styled(Box)`
    position: relative;
    width: 100px; // Set width
    height: 150px; // Set height for 2:3 ratio (100 * 1.5)
    border: 1px solid ${THEME_MAP["color-basic-400"]};
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0; // Prevent shrinking

    img {
        width: 100%;
        height: 100%;
        object-fit: cover; // Cover ensures the image fills the container
    }

    .delete-button {
        position: absolute;
        top: 2px;
        right: 2px;
        background-color: rgba(255, 255, 255, 0.8);
        &:hover {
            background-color: rgba(255, 255, 255, 1);
        }
    }
`;

const PosterOptionsContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MAX_HAPPY_HOUR_ITEMS = 7;
const MAX_WEEKLY_SPECIALS = 5;
const POSTER_FOLDER = "Bar_Special_Posters";

// Helper to format price string
const formatPrice = (priceStr) => {
    if (!priceStr || typeof priceStr !== "string") return "0.00";
    const num = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return "0.00";
    // Check if it has decimals already
    if (priceStr.includes(".")) {
        const parts = priceStr.split(".");
        const decimalPart = parts[1] || "";
        if (decimalPart.length === 1) return `${parts[0]}.${decimalPart}0`; // Add trailing zero if needed
        if (decimalPart.length >= 2) return `${parts[0]}.${decimalPart.substring(0, 2)}`; // Truncate extra decimals
    }
    return num.toFixed(2); // Add .00 if no decimals
};

// Helper to format time from Dayjs object
const formatTime = (timeObj) => {
    return timeObj ? dayjs(timeObj).format("h:mm A") : null;
};

export function BarSpecials() {
    const { id: barId } = useParams(); // Get barId from route
    // Removed unused history variable
    // const history = useHistory();
    const selectedBar = useViewedBar(); // Get selected bar data (includes name)
    const { fetchBarById } = useBarUtils();

    const [specialType, setSpecialType] = useState("");
    const [isLoading, setIsLoading] = useState(true); // For initial data load
    const [isSaving, setIsSaving] = useState(false); // For saving state
    const [hasExistingSpecials, setHasExistingSpecials] = useState(false); // Whether a saved doc exists
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // --- Happy Hour State ---
    const [happyHourDaysType, setHappyHourDaysType] = useState("specific");
    const [happyHourSpecificDays, setHappyHourSpecificDays] = useState(
        daysOfWeek.reduce((acc, day) => ({ ...acc, [day.toUpperCase()]: false }), {}) // Use uppercase keys to match mobile format potentially
    );
    const [happyHourStartDay, setHappyHourStartDay] = useState("");
    const [happyHourEndDay, setHappyHourEndDay] = useState("");
    const [happyHourStartTime, setHappyHourStartTime] = useState(null);
    const [happyHourEndTime, setHappyHourEndTime] = useState(null);
    const [happyHourItems, setHappyHourItems] = useState([{ price: "", description: "" }]);

    // --- Weekly Specials State ---
    const [weeklySpecials, setWeeklySpecials] = useState([{
        id: Date.now(), dayType: "specific", specificDay: "", startDay: "", endDay: "",
        startTime: null, endTime: null, description: ""
    }]);

    // --- Image Upload State ---
    const fileSelectRef = useRef(null);
    // Store { file?: File, preview: string, displayUntil: Date | null, isIndefinite: boolean, existingUrl?: string, id?: string }
    const [uploadedImages, setUploadedImages] = useState([]);

    // --- Fetch Existing Data ---
    useEffect(() => {
        if (barId && (!selectedBar || selectedBar.barId !== barId)) {
            fetchBarById(barId).catch((error) => {
                console.error("Error fetching bar from Supabase:", error);
                captureError(error, { action: "fetchBarForSpecials", barId });
            });
        }
    }, [barId, fetchBarById, selectedBar]);

    useEffect(() => {
        if (!barId) {
            setIsLoading(false);
            setSnackbar({ open: true, message: "Bar ID not found.", severity: "error" });
            // Consider redirecting or showing a more permanent error
            return;
        }

        const fetchSpecials = async () => {
            setIsLoading(true);
            try {
                const docRef = firestore.collection("BarSpecials").doc(barId);
                const docSnap = await docRef.get();

                if (docSnap.exists) {
                    const data = docSnap.data();
                    setHasExistingSpecials(true);
                    // Determine specialType based on fetched data
                    const hasHappyHour = !!data.happyHour;
                    const hasWeekly = !!data.weeklySpecials && data.weeklySpecials.length > 0;
                    if (hasHappyHour && hasWeekly) setSpecialType("both");
                    else if (hasHappyHour) setSpecialType("happyHour");
                    else if (hasWeekly) setSpecialType("weekly");
                    else setSpecialType(""); // Or default if needed

                    // Populate Happy Hour State
                    if (data.happyHour) {
                        const hh = data.happyHour;
                        if (Array.isArray(hh.days)) {
                            setHappyHourDaysType("specific");
                            const specificDaysState = daysOfWeek.reduce((acc, day) => ({ ...acc, [day.toUpperCase()]: false }), {});
                            hh.days.forEach(day => {
                                if (Object.prototype.hasOwnProperty.call(specificDaysState, day.toUpperCase())) {
                                    specificDaysState[day.toUpperCase()] = true;
                                }
                            });
                            setHappyHourSpecificDays(specificDaysState);
                        } else if (typeof hh.days === "object" && hh.days.startDay && hh.days.endDay) {
                            setHappyHourDaysType("range");
                            setHappyHourStartDay(hh.days.startDay);
                            setHappyHourEndDay(hh.days.endDay);
                        }
                        // Parse time strings back to Dayjs objects for TimePicker
                        setHappyHourStartTime(hh.startTime ? dayjs(hh.startTime, "h:mm A") : null);
                        setHappyHourEndTime(hh.endTime ? dayjs(hh.endTime, "h:mm A") : null);
                        // Parse specials string back into price/description
                        setHappyHourItems(hh.specials.map(s => {
                            const priceMatch = s.match(/^\$(\d+\.?\d*)\s/);
                            const price = priceMatch ? priceMatch[1] : "";
                            const description = priceMatch ? s.substring(priceMatch[0].length) : s;
                            return { price, description };
                        }));
                    }

                    // Populate Weekly Specials State
                    if (data.weeklySpecials) {
                        setWeeklySpecials(data.weeklySpecials.map((ws, index) => ({
                            id: Date.now() + index, // Ensure unique ID for local state
                            dayType: ws.days ? "range" : "specific",
                            specificDay: ws.day || "",
                            startDay: ws.days?.startDay || "",
                            endDay: ws.days?.endDay || "",
                            startTime: ws.startTime ? dayjs(ws.startTime, "h:mm A") : null,
                            endTime: ws.endTime ? dayjs(ws.endTime, "h:mm A") : null,
                            description: ws.description || ""
                        })));
                    }

                    // Populate Posters State
                    if (data.posters) {
                        setUploadedImages(data.posters.map(p => ({
                            id: p.id, // Keep original ID (filename)
                            preview: p.url,
                            existingUrl: p.url, // Mark as existing
                            displayUntil: p.displayUntil ? p.displayUntil.toDate() : null,
                            isIndefinite: !p.displayUntil,
                        })));
                    }

                } else {
                    // No existing specials, form is empty by default
                    setHasExistingSpecials(false);
                    console.log("No existing specials document found for this bar.");
                }
            } catch (error) {
                console.error("Error fetching bar specials:", error);
                captureError(error, { action: "fetchBarSpecials", barId });
                setSnackbar({ open: true, message: "Failed to load existing specials.", severity: "error" });
            } finally {
                setIsLoading(false);
            }
        };

        fetchSpecials();
    }, [barId]);


    // --- Handlers ---
    const handleSpecialTypeChange = useCallback((event) => {
        setSpecialType(event.target.value);
    }, []);

    // --- Happy Hour Handlers ---
    const handleHappyHourDayTypeToggle = useCallback((isRange) => {
        const newDayType = isRange ? "range" : "specific";
        setHappyHourDaysType(newDayType);
        if (isRange) {
            setHappyHourSpecificDays(daysOfWeek.reduce((acc, day) => ({ ...acc, [day.toUpperCase()]: false }), {}));
        } else {
            setHappyHourStartDay("");
            setHappyHourEndDay("");
        }
    }, []);

    const handleHappyHourSpecificDayChange = useCallback((event) => {
        setHappyHourSpecificDays(prev => ({
            ...prev,
            [event.target.name.toUpperCase()]: event.target.checked, // Ensure uppercase key
        }));
    }, []);

    const handleAddHappyHourItem = useCallback(() => {
        if (happyHourItems.length < MAX_HAPPY_HOUR_ITEMS) {
            setHappyHourItems(prev => [...prev, { price: "", description: "" }]);
        }
    }, [happyHourItems.length]);

    const handleRemoveHappyHourItem = useCallback((index) => {
        if (happyHourItems.length > 1) {
            setHappyHourItems(prev => prev.filter((_, i) => i !== index));
        }
    }, [happyHourItems.length]);

    const handleHappyHourItemChange = useCallback((index, field, value) => {
        setHappyHourItems(prev => {
            const updatedItems = [...prev];
            updatedItems[index] = { ...updatedItems[index], [field]: value };
            return updatedItems;
        });
    }, []);

    // --- Weekly Specials Handlers ---
    const handleAddWeeklySpecial = useCallback(() => {
        if (weeklySpecials.length < MAX_WEEKLY_SPECIALS) {
            setWeeklySpecials(prev => [...prev, {
                id: Date.now(), dayType: "specific", specificDay: "", startDay: "", endDay: "",
                startTime: null, endTime: null, description: ""
            }]);
        }
    }, [weeklySpecials.length]);

    const handleRemoveWeeklySpecial = useCallback((id) => {
        if (weeklySpecials.length > 1) {
            setWeeklySpecials(prev => prev.filter(special => special.id !== id));
        }
    }, [weeklySpecials.length]);

    const handleWeeklySpecialChange = useCallback((id, field, value) => {
        setWeeklySpecials(prev => prev.map(special =>
            special.id === id ? { ...special, [field]: value } : special
        ));
    }, []);

    const handleWeeklyDayTypeChange = useCallback((id, isRange) => {
        const newDayType = isRange ? "range" : "specific";
        setWeeklySpecials(prev => prev.map(special => {
            if (special.id === id) {
                const resetState = isRange
                    ? { specificDay: "" }
                    : { startDay: "", endDay: "" };
                return { ...special, dayType: newDayType, ...resetState };
            }
            return special;
        }));
    }, []);

    // --- Image Upload Handlers ---
    const clickFileUpload = useCallback(() => fileSelectRef.current?.click(), []);

    const handleFileSelect = useCallback((event) => {
        const files = Array.from(event.target.files);
        const imageFiles = files.filter(file => file.type.startsWith("image/"));

        // Default expiration date (e.g., 1 month from now)
        const defaultExpiration = dayjs().add(1, "month").toDate();

        const newImages = imageFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            displayUntil: defaultExpiration, // Default expiration date
            isIndefinite: false, // Default to NOT indefinite
        }));

        setUploadedImages(prev => [...prev, ...newImages]);

        if (fileSelectRef.current) {
            fileSelectRef.current.value = "";
        }
    }, []);

    const handleRemoveImage = useCallback((indexToRemove) => {
        setUploadedImages(prev => {
            const imageToRemove = prev[indexToRemove];
            // Revoke object URL only if it's a newly uploaded file preview
            if (imageToRemove && imageToRemove.file && imageToRemove.preview.startsWith("blob:")) {
                URL.revokeObjectURL(imageToRemove.preview);
            }
            return prev.filter((_, index) => index !== indexToRemove);
        });
    }, []);

    const handlePosterDateChange = useCallback((index, newValue) => {
        setUploadedImages(prev => prev.map((img, i) =>
            // Ensure setting a date unchecks indefinite
            i === index ? { ...img, displayUntil: newValue, isIndefinite: !newValue } : img
        ));
    }, []);

    const handlePosterIndefiniteChange = useCallback((index, checked) => {
        setUploadedImages(prev => prev.map((img, i) =>
            // If checked, clear the date; otherwise, keep existing date or set a default if null
            i === index ? { ...img, isIndefinite: checked, displayUntil: checked ? null : (img.displayUntil || dayjs().add(1, "month").toDate()) } : img
        ));
    }, []);

    // --- Clear Handlers (reset a special type; persists once the user saves) ---
    const handleClearHappyHour = useCallback(() => {
        setHappyHourDaysType("specific");
        setHappyHourSpecificDays(daysOfWeek.reduce((acc, day) => ({ ...acc, [day.toUpperCase()]: false }), {}));
        setHappyHourStartDay("");
        setHappyHourEndDay("");
        setHappyHourStartTime(null);
        setHappyHourEndTime(null);
        setHappyHourItems([{ price: "", description: "" }]);
        setSpecialType(prev => (prev === "both" ? "weekly" : ""));
        setSnackbar({ open: true, message: "Happy Hour cleared. Press Save to apply.", severity: "info" });
    }, []);

    const handleClearWeekly = useCallback(() => {
        setWeeklySpecials([{
            id: Date.now(), dayType: "specific", specificDay: "", startDay: "", endDay: "",
            startTime: null, endTime: null, description: ""
        }]);
        setSpecialType(prev => (prev === "both" ? "happyHour" : ""));
        setSnackbar({ open: true, message: "Weekly Specials cleared. Press Save to apply.", severity: "info" });
    }, []);

    // --- Save Handler ---
    const handleSave = useCallback(async () => {
        if (!barId) {
            setSnackbar({ open: true, message: "Cannot save: Bar ID is missing.", severity: "error" });
            return;
        }
        if (!specialType && uploadedImages.length === 0 && !hasExistingSpecials) {
            setSnackbar({ open: true, message: "Add a Happy Hour, Weekly Special, or poster before saving.", severity: "warning" });
            return;
        }

        setIsSaving(true);
        setSnackbar({ open: false, message: "", severity: "info" }); // Clear previous snackbar

        try {
            const barName = selectedBar?.barName || selectedBar?.bar_name || "Unknown Bar"; // Get bar name
            const specialsData = {
                barId: barId,
                barName: barName,
                happyHour: null,
                weeklySpecials: [],
                posters: [],
            };

            // --- Format Happy Hour ---
            if (specialType === "happyHour" || specialType === "both") {
                // Validation
                const isSpecificDaysSelected = Object.values(happyHourSpecificDays).some(v => v);
                const isRangeValid = happyHourStartDay && happyHourEndDay;
                if (happyHourDaysType === "specific" && !isSpecificDaysSelected) throw new Error("Happy Hour: Please select at least one specific day.");
                if (happyHourDaysType === "range" && !isRangeValid) throw new Error("Happy Hour: Please select both start and end days for the range.");
                if (!happyHourStartTime || !happyHourEndTime) throw new Error("Happy Hour: Please select both start and end times.");
                if (dayjs(happyHourEndTime).isSameOrBefore(dayjs(happyHourStartTime))) throw new Error("Happy Hour: End time must be after start time.");
                if (happyHourItems.some(item => !item.price || !item.description)) throw new Error("Happy Hour: All special items must have both a price and description.");

                specialsData.happyHour = {
                    days: happyHourDaysType === "range"
                        ? { startDay: happyHourStartDay, endDay: happyHourEndDay }
                        : Object.entries(happyHourSpecificDays)
                            // eslint-disable-next-line no-unused-vars
                            .filter(([_, value]) => value)
                            .map(([key]) => key), // Get array of selected day keys (uppercase)
                    startTime: formatTime(happyHourStartTime),
                    endTime: formatTime(happyHourEndTime),
                    specials: happyHourItems.map(item => `$${formatPrice(item.price)} ${item.description.trim()}`)
                };
            }

            // --- Format Weekly Specials ---
            if (specialType === "weekly" || specialType === "both") {
                if (weeklySpecials.some(ws => !ws.description)) throw new Error("Weekly Specials: All specials must have a description.");
                if (weeklySpecials.some(ws => ws.dayType === "specific" && !ws.specificDay)) throw new Error("Weekly Specials: Please select a day for each specific day special.");
                if (weeklySpecials.some(ws => ws.dayType === "range" && (!ws.startDay || !ws.endDay))) throw new Error("Weekly Specials: Please select start and end days for each range special.");
                if (weeklySpecials.some(ws => (ws.startTime && !ws.endTime) || (!ws.startTime && ws.endTime))) throw new Error("Weekly Specials: If specifying time, both start and end times are required.");
                if (weeklySpecials.some(ws => ws.startTime && ws.endTime && dayjs(ws.endTime).isSameOrBefore(dayjs(ws.startTime)))) throw new Error("Weekly Specials: End time must be after start time.");

                specialsData.weeklySpecials = weeklySpecials.map(ws => {
                    const weeklySpecialItem = {
                        description: ws.description.trim(),
                        startTime: formatTime(ws.startTime),
                        endTime: formatTime(ws.endTime),
                    };
                    if (ws.dayType === "range") {
                        weeklySpecialItem.days = { startDay: ws.startDay, endDay: ws.endDay };
                    } else {
                        weeklySpecialItem.day = ws.specificDay;
                    }
                    // Only include times if both are set
                    if (!weeklySpecialItem.startTime || !weeklySpecialItem.endTime) {
                        delete weeklySpecialItem.startTime;
                        delete weeklySpecialItem.endTime;
                    }
                    return weeklySpecialItem;
                });
            }

            // --- Handle Posters ---
            const posterUploadPromises = uploadedImages.map(async (img) => {
                let url = img.existingUrl;
                let posterId = img.id || generateId(); // Use existing ID or generate new one

                // If it's a new file, upload it
                if (img.file) {
                    const fileExt = img.file.name.split(".").pop();
                    const fileName = `${posterId}.${fileExt}`;
                    const filePath = `${POSTER_FOLDER}/${barId}/${fileName}`;
                    const storageRef = storage.ref(filePath);
                    const uploadTask = await storageRef.put(img.file);
                    url = await uploadTask.ref.getDownloadURL();
                    posterId = fileName; // Update ID to be the filename
                }

                if (!url) return null; // Skip if somehow URL is missing (e.g., removed existing without new upload)

                return {
                    id: posterId,
                    url: url,
                    displayUntil: img.isIndefinite || !img.displayUntil
                        ? null
                        : firebase.firestore.Timestamp.fromDate(dayjs(img.displayUntil).endOf("day").toDate()), // Set to end of day
                };
            });

            const posterResults = (await Promise.all(posterUploadPromises)).filter(p => p !== null);
            specialsData.posters = posterResults;

            // --- Save to Firestore ---
            const docRef = firestore.collection("BarSpecials").doc(barId);
            await docRef.set(specialsData, { merge: true }); // Use merge to avoid overwriting other fields if any

            setSnackbar({ open: true, message: "Specials saved successfully!", severity: "success" });
            setHasExistingSpecials(true);
            // Optionally redirect or clear form
            // history.push(`/bar/${barId}`); // Example redirect

        } catch (error) {
            console.error("Error saving specials:", error);
            captureError(error, { action: "saveBarSpecials", barId });
            setSnackbar({ open: true, message: `Save failed: ${error.message}`, severity: "error" });
        } finally {
            setIsSaving(false);
        }
    }, [
        barId, specialType, selectedBar,
        happyHourDaysType, happyHourSpecificDays, happyHourStartDay, happyHourEndDay, happyHourStartTime, happyHourEndTime, happyHourItems,
        weeklySpecials, uploadedImages, hasExistingSpecials
    ]);

    // --- Close Snackbar Handler ---
    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // --- Render Logic ---

    if (isLoading) {
        return (
            <StyledContainer style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </StyledContainer>
        );
    }

    const renderHappyHourSection = () => (
        <SectionCard>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <SectionTitle variant="h6">Happy Hour</SectionTitle>
                    <Button color="error" size="small" startIcon={<Delete />} onClick={handleClearHappyHour}>
                        Clear
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    {/* Day Selection */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={happyHourDaysType === "range"}
                                    onChange={(e) => handleHappyHourDayTypeToggle(e.target.checked)}
                                />
                            }
                            label="Use Range of Days"
                        />
                    </Grid>

                    {happyHourDaysType === "specific" && (
                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ mb: 1 }}>Specific Days *</FormLabel>
                            <FormGroup row>
                                {daysOfWeek.map(day => (
                                    <FormControlLabel
                                        key={day}
                                        // Use uppercase day for name and check state key
                                        control={<Checkbox checked={happyHourSpecificDays[day.toUpperCase()] || false} onChange={handleHappyHourSpecificDayChange} name={day.toUpperCase()} />}
                                        label={day.substring(0, 3)}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>
                    )}
                    {happyHourDaysType === "range" && (
                        <>
                            <Grid item xs={12}>
                                <FormLabel component="legend" sx={{ mb: 1 }}>Range of Days *</FormLabel>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <FormControl fullWidth margin="dense" required>
                                    {/* Ensure value matches state keys (uppercase) */}
                                    <Select value={happyHourStartDay} onChange={(e) => setHappyHourStartDay(e.target.value)} displayEmpty>
                                        <MenuItem value="" disabled>Start Day *</MenuItem>
                                        {daysOfWeek.map(day => <MenuItem key={day} value={day.toUpperCase()}>{day}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2} sx={{ textAlign: "center", alignSelf: "center" }}>
                                <Typography>-</Typography>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <FormControl fullWidth margin="dense" required>
                                    <Select value={happyHourEndDay} onChange={(e) => setHappyHourEndDay(e.target.value)} displayEmpty>
                                        <MenuItem value="" disabled>End Day *</MenuItem>
                                        {daysOfWeek.map(day => <MenuItem key={day} value={day.toUpperCase()}>{day}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    {/* Time Selection */}
                    <Grid item xs={12}>
                        <FormLabel component="legend" sx={{ mt: 2 }}>Time *</FormLabel>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TimePicker
                            label="Start Time"
                            value={happyHourStartTime}
                            onChange={(newValue) => setHappyHourStartTime(newValue)}
                            slotProps={{ textField: { fullWidth: true, margin: "dense", required: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ textAlign: "center", alignSelf: "center" }}>
                        <Typography>-</Typography>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TimePicker
                            label="End Time"
                            value={happyHourEndTime}
                            onChange={(newValue) => setHappyHourEndTime(newValue)}
                            slotProps={{ textField: { fullWidth: true, margin: "dense", required: true } }}
                        />
                    </Grid>
                </Grid>

                {/* Items */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle1" gutterBottom>Specials *</Typography>
                {happyHourItems.map((item, index) => (
                    <ItemContainer key={index}>
                        <TextField
                            label="Price"
                            value={item.price}
                            onChange={(e) => handleHappyHourItemChange(index, "price", e.target.value)}
                            // Apply formatting on blur or before save, not directly on change for better UX
                            // onBlur={(e) => handleHappyHourItemChange(index, "price", formatPrice(e.target.value))}
                            required
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            sx={{ width: "150px", flexShrink: 0 }}
                            margin="dense"
                            type="text"
                            inputProps={{ inputMode: "decimal" }}
                        />
                        <TextField
                            label="Item Description"
                            value={item.description}
                            onChange={(e) => handleHappyHourItemChange(index, "description", e.target.value)}
                            required
                            fullWidth
                            margin="dense"
                        />
                        <Tooltip title="Remove Item">
                            <span>
                                <IconButton
                                    onClick={() => handleRemoveHappyHourItem(index)}
                                    color="error"
                                    disabled={happyHourItems.length <= 1}
                                    sx={{ mt: 1 }}
                                >
                                    <Delete />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </ItemContainer>
                ))}
                {happyHourItems.length < MAX_HAPPY_HOUR_ITEMS && (
                    <AddItemButton variant="outlined" startIcon={<Add />} onClick={handleAddHappyHourItem}>
                        Add Another Happy Hour Item
                    </AddItemButton>
                )}
            </CardContent>
        </SectionCard>
    );

    const renderWeeklySpecialsSection = () => (
        <SectionCard>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <SectionTitle variant="h6">Weekly Specials</SectionTitle>
                    <Button color="error" size="small" startIcon={<Delete />} onClick={handleClearWeekly}>
                        Clear
                    </Button>
                </Box>
                {weeklySpecials.map((special, index) => (
                    <Box key={special.id} mb={3} p={2} border={1} borderColor="divider" borderRadius={1} position="relative">
                        <Tooltip title="Remove Special">
                            <span>
                            <IconButton
                                onClick={() => handleRemoveWeeklySpecial(special.id)}
                                color="error"
                                disabled={weeklySpecials.length <= 1}
                                size="small"
                                sx={{ position: "absolute", top: 8, right: 8 }}
                            >
                                <Delete fontSize='small' />
                            </IconButton>
                            </span>
                        </Tooltip>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>Special #{index + 1}</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={special.dayType === "range"}
                                            onChange={(e) => handleWeeklyDayTypeChange(special.id, e.target.checked)}
                                        />
                                    }
                                    label="Use Range of Days"
                                />
                            </Grid>

                            {special.dayType === "specific" && (
                                <Grid item xs={12} sm={6}>
                                    <FormLabel component="legend" sx={{ mb: 1 }}>Specific Day *</FormLabel>
                                    <FormControl fullWidth margin="dense" required>
                                        <Select
                                            value={special.specificDay}
                                            onChange={(e) => handleWeeklySpecialChange(special.id, "specificDay", e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="" disabled>Select Day *</MenuItem>
                                            {/* Ensure value matches state keys (uppercase) */}
                                            {daysOfWeek.map(day => <MenuItem key={day} value={day.toUpperCase()}>{day}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {special.dayType === "range" && (
                                <>
                                    <Grid item xs={12}>
                                        <FormLabel component="legend" sx={{ mb: 1 }}>Range of Days *</FormLabel>
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <FormControl fullWidth margin="dense" required>
                                            <Select value={special.startDay} onChange={(e) => handleWeeklySpecialChange(special.id, "startDay", e.target.value)} displayEmpty>
                                                <MenuItem value="" disabled>Start Day *</MenuItem>
                                                {daysOfWeek.map(day => <MenuItem key={day} value={day.toUpperCase()}>{day}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={2} sx={{ textAlign: "center", alignSelf: "center" }}>
                                        <Typography>-</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <FormControl fullWidth margin="dense" required>
                                            <Select value={special.endDay} onChange={(e) => handleWeeklySpecialChange(special.id, "endDay", e.target.value)} displayEmpty>
                                                <MenuItem value="" disabled>End Day *</MenuItem>
                                                {daysOfWeek.map(day => <MenuItem key={day} value={day.toUpperCase()}>{day}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}

                            {/* Optional Time */}
                            <Grid item xs={12}>
                                <FormLabel component="legend" sx={{ mt: 2 }}>Time (Optional)</FormLabel>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TimePicker
                                    label="Start Time"
                                    value={special.startTime}
                                    onChange={(newValue) => handleWeeklySpecialChange(special.id, "startTime", newValue)}
                                    slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} sx={{ textAlign: "center", alignSelf: "center" }}>
                                <Typography>-</Typography>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TimePicker
                                    label="End Time"
                                    value={special.endTime}
                                    onChange={(newValue) => handleWeeklySpecialChange(special.id, "endTime", newValue)}
                                    slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
                                />
                            </Grid>

                            {/* Description */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Weekly Special Description"
                                    value={special.description}
                                    onChange={(e) => handleWeeklySpecialChange(special.id, "description", e.target.value)}
                                    required
                                    fullWidth
                                    multiline
                                    rows={2}
                                    margin="dense"
                                />
                            </Grid>
                        </Grid>
                        {index < weeklySpecials.length - 1 && <Divider sx={{ mt: 3 }} />}
                    </Box>
                ))}

                {weeklySpecials.length < MAX_WEEKLY_SPECIALS && (
                    <AddItemButton variant="outlined" startIcon={<Add />} onClick={handleAddWeeklySpecial}>
                        Add Another Weekly Special
                    </AddItemButton>
                )}
            </CardContent>
        </SectionCard>
    );

    const renderImageUploadSection = () => (
        <SectionCard data-tour="specials-poster">
            <CardContent>
                {/* Title and Upload Button Side-by-Side */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <SectionTitle variant="h6" sx={{ mb: 0 }}> {/* Remove bottom margin */}
                        Event/Special Posters
                    </SectionTitle>
                    <Button
                        variant="outlined"
                        onClick={clickFileUpload}
                        startIcon={<Add />}
                        disabled={isSaving}
                        size="small" // Optional: make button smaller
                    >
                        Upload Image(s)
                    </Button>
                </Box>

                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Upload images for posters or flyers (optional). Set an expiration date or mark as indefinite.
                </Typography>
                <Box display="flex" flexDirection="column" gap={2} my={2}>
                    {uploadedImages.map((img, index) => (
                        <ImageUploadItemContainer key={img.id || index}>
                            <ImagePreviewContainer>
                                <img
                                    src={img.preview} // Use preview URL (blob or existing URL)
                                    alt={`upload preview ${index}`}
                                />
                                <Tooltip title="Remove Image">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveImage(index)}
                                        className="delete-button"
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </ImagePreviewContainer>
                            <PosterOptionsContainer>
                                <DatePicker
                                    label="Display Until"
                                    value={img.displayUntil ? dayjs(img.displayUntil) : null}
                                    onChange={(newValue) => handlePosterDateChange(index, newValue ? newValue.toDate() : null)}
                                    disabled={img.isIndefinite}
                                    minDate={dayjs()} // Prevent selecting past dates
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            helperText: img.isIndefinite ? "Does not expire" : "Poster will hide after this date",
                                            required: !img.isIndefinite // Mark as required if not indefinite
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={img.isIndefinite}
                                            onChange={(e) => handlePosterIndefiniteChange(index, e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="No Expiration"
                                />
                            </PosterOptionsContainer>
                        </ImageUploadItemContainer>
                    ))}
                </Box>

                {/* Hidden file input */}
                <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif, image/webp" // Added webp
                    ref={fileSelectRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    multiple
                />
            </CardContent>
        </SectionCard>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StyledContainer>
                <Typography variant="h4" gutterBottom component="h1">
                    {selectedBar?.barName || selectedBar?.bar_name || "Bar"}
                </Typography>

                <FormControl component="fieldset" margin="normal" required sx={{ mb: 3 }} data-tour="specials-type">
                    <FormLabel component="legend">Select Specials to Offer</FormLabel>
                    <RadioGroup row value={specialType} onChange={handleSpecialTypeChange}>
                        <FormControlLabel value="happyHour" control={<Radio />} label="Happy Hour Only" />
                        <FormControlLabel value="weekly" control={<Radio />} label="Weekly Specials Only" />
                        <FormControlLabel value="both" control={<Radio />} label="Both" />
                    </RadioGroup>
                    {!specialType && uploadedImages.length === 0 && <Typography color="error" variant="caption">Select a special type, or just add a poster below.</Typography>}
                </FormControl>

                {(specialType === "happyHour" || specialType === "both") && renderHappyHourSection()}
                {(specialType === "weekly" || specialType === "both") && renderWeeklySpecialsSection()}

                {renderImageUploadSection()}

                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 1, mb: 3, minWidth: 120 }}
                    disabled={isSaving || isLoading || (!specialType && uploadedImages.length === 0 && !hasExistingSpecials)}
                    onClick={handleSave}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                    data-tour="specials-save"
                >
                    {isSaving ? "Saving..." : "Save Specials"}
                </Button>

                {/* Feedback Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </StyledContainer>
        </LocalizationProvider>
    );
}

// Make sure generateId is available (copied from actions/bars.js if not exported)
// const generateId = () => {
// 	const chars =
// 		"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
// 	let autoId = "";
// 	for (let i = 0; i < 20; i++) {
// 		autoId += chars.charAt(
// 			Math.floor(Math.random() * chars.length)
// 		);
// 	}
// 	return autoId;
// };